/**
 * GameStartModal - ゲーム開始前のルール説明モーダル
 *
 * 設定駆動: マップ固有の判定をせず、config から渡された設定に基づいて表示
 *
 * テキスト取得:
 * - 共通キー: t.gameStartCommon から取得（title, designedBy, victoryCondition, specialRules, etc.）
 * - マップ固有キー: t[config.i18nKey] から取得（description, gameEnd, loseCondition, tips, etc.）
 */

import { useState, useEffect } from 'react';
import { useI18n, interpolate, type Language } from '../i18n';
import { getUIConfigByMapName } from '../config/gameRegistry';
import type { PuertoRicoDifficulty } from '../types';
import { DIFFICULTY_CUBES } from '../types';
import { debug } from '../utils/debug';
import { BASE_PATH } from '../config';

export interface GameStartModalProps {
  isOpen: boolean;
  onStartGame: () => void;
  mapName?: string;
  /** ルール確認モード（ゲーム中に設定から開いた場合） */
  isRulesOnly?: boolean;
}

/** 難易度リスト */
const DIFFICULTIES: PuertoRicoDifficulty[] = ['Beginner', 'Novice', 'Expert', 'Master', 'Conqueror', 'God'];
const DIFFICULTY_STORAGE_KEY = 'puerto_rico_difficulty';
const AUTO_START_KEY = 'puerto_rico_auto_start';

export function GameStartModal({ isOpen, onStartGame, mapName, isRulesOnly = false }: GameStartModalProps) {
  const { language, setLanguage, t } = useI18n();

  // 難易度設定（Puerto Rico用）- localStorage から初期値を取得
  const getStoredDifficulty = (): PuertoRicoDifficulty => {
    if (typeof localStorage === 'undefined') return 'Expert';
    const stored = localStorage.getItem(DIFFICULTY_STORAGE_KEY);
    if (stored && DIFFICULTIES.includes(stored as PuertoRicoDifficulty)) {
      return stored as PuertoRicoDifficulty;
    }
    return 'Expert';
  };

  const [selectedDifficulty, setSelectedDifficulty] = useState<PuertoRicoDifficulty>(getStoredDifficulty);
  const [initialDifficulty] = useState<PuertoRicoDifficulty>(getStoredDifficulty);

  // リロード後の自動開始チェック
  useEffect(() => {
    if (isOpen && typeof localStorage !== 'undefined') {
      const autoStart = localStorage.getItem(AUTO_START_KEY);
      if (autoStart === 'true') {
        debug.log('[GameStartModal] Auto-starting after difficulty change reload');
        localStorage.removeItem(AUTO_START_KEY);
        onStartGame();
      }
    }
  }, [isOpen, onStartGame]);

  if (!isOpen) return null;

  // 設定を取得（designer, i18nMapKey 含む）
  const { startModal: config, designer, i18nMapKey } = getUIConfigByMapName(mapName || '');

  // 共通キー取得ヘルパー
  const getCommonText = (key: string): string => {
    return (t.gameStartCommon as any)[key] || key;
  };

  // マップ固有キー取得ヘルパー（フォールバック: 共通キー）
  const getMapText = (key: string): string => {
    const mapI18n = (t as any)[config.i18nKey];
    if (mapI18n && mapI18n[key] !== undefined) {
      return mapI18n[key];
    }
    return (t.gameStartCommon as any)[key] || key;
  };

  const handleLanguageChange = (newLang: Language) => {
    setLanguage(newLang);
  };

  // ゲーム開始ハンドラー（難易度変更時はリロードで再setup）
  const handleStartGame = () => {
    if (config.hasDifficultySelection && selectedDifficulty !== initialDifficulty) {
      // 難易度が変更された場合: localStorage に保存してリロード → 自動開始
      debug.log(`[GameStartModal] Difficulty changed: ${initialDifficulty} -> ${selectedDifficulty}, reloading...`);
      localStorage.setItem(DIFFICULTY_STORAGE_KEY, selectedDifficulty);
      localStorage.setItem(AUTO_START_KEY, 'true');
      window.location.reload();
    } else {
      // 変更なし: そのまま開始
      onStartGame();
    }
  };

  // マップ固有のタイトルを生成（i18n対応）
  const localizedMapName = t.mapSelection.maps[i18nMapKey].name;
  const mapTitle = mapName
    ? `${getCommonText('title')} - ${localizedMapName}`
    : getCommonText('title');

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/60 backdrop-blur-sm p-2"
      role="dialog"
      aria-modal="true"
      aria-label="Game Rules"
    >
      <div className="bg-card rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-scale-in">
        {/* ヘッダー */}
        <div className="p-4 border-b border-border">
          <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground text-center">
            {mapTitle}
          </h1>
          {/* デザイナー名 */}
          <p className="text-sm text-muted-foreground text-center mt-1">
            {getCommonText('designedBy')} {designer}
          </p>

          {/* 言語選択 */}
          <div className="mt-3 flex items-center justify-center gap-2">
            <span className="text-xs text-muted-foreground">
              表示言語選択 / Language:
            </span>
            <div className="flex gap-1">
              <button
                className={`px-2 py-1 text-xs rounded transition-all ${
                  language === 'en'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
                onClick={() => handleLanguageChange('en')}
              >
                English
              </button>
              <button
                className={`px-2 py-1 text-xs rounded transition-all ${
                  language === 'ja'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
                onClick={() => handleLanguageChange('ja')}
              >
                日本語
              </button>
            </div>
          </div>
        </div>

        {/* ルール説明 */}
        <div className="p-4 space-y-4 text-sm">
          {/* 概要 */}
          <section>
            <p className="text-muted-foreground">
              {getMapText('description')}
            </p>
          </section>

          {/* 勝利条件 */}
          <section className={`rounded-lg p-3 border ${
            config.victoryStyle === 'warning'
              ? 'bg-warning/10 border-warning/30'
              : 'bg-success/10 border-success/30'
          }`}>
            <h2 className={`font-heading font-semibold text-base mb-2 ${
              config.victoryStyle === 'warning' ? 'text-warning' : 'text-success'
            }`}>
              {getCommonText('victoryCondition')}
            </h2>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              {/* 終了条件 */}
              <li>{getMapText('gameEnd')}</li>
              {/* 勝利条件（あれば） */}
              {config.victory.winConditionKey && (
                <li className="text-success">{getMapText(config.victory.winConditionKey)}</li>
              )}
              {/* 追加項目 */}
              {config.victory.additionalItems?.map((key, i) => (
                <li key={i}>{getMapText(key)}</li>
              ))}
              {/* 敗北条件 */}
              <li className="text-destructive">{getMapText('loseCondition')}</li>
            </ul>
          </section>

          {/* 特殊ルール */}
          <section>
            <h2 className="font-heading font-semibold text-base mb-2 text-primary">
              {getCommonText('specialRules')}
            </h2>
            <div className="space-y-3">
              {config.phases.map((phase, i) => (
                <div key={i}>
                  <div className="flex items-start gap-2">
                    <span className="bg-primary/20 text-primary text-xs font-medium px-2 py-0.5 rounded">
                      {interpolate(getCommonText('phase'), { num: phase.num })}
                    </span>
                    <div>
                      {phase.ruleKey && (
                        <span className="font-medium">{getMapText(phase.ruleKey)}</span>
                      )}
                      <span className="text-muted-foreground"> {getMapText(phase.descKey)}</span>
                      {phase.actionsKey && (
                        <span className="text-foreground"> {getMapText(phase.actionsKey)}</span>
                      )}
                    </div>
                  </div>
                  {phase.extraKeys && phase.extraKeys.length > 0 && (
                    <div className="ml-9 p-2 bg-muted/50 rounded text-xs text-muted-foreground mt-1">
                      {phase.extraKeys.map((key, j) => (
                        <p key={j}>{getMapText(key)}</p>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* 特殊リンク（設定がある場合のみ） */}
          {config.specialLinks && config.specialLinks.length > 0 && (
            <section>
              <h2 className="font-heading font-semibold text-base mb-2 text-primary">
                {getMapText('specialLinksTitle')}
              </h2>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                {config.specialLinks.map((key, i) => (
                  <li key={i}>{getMapText(key)}</li>
                ))}
              </ul>
            </section>
          )}

          {/* 戦略ヒント */}
          <section className="bg-muted/30 rounded-lg p-3">
            <h2 className="font-heading font-semibold text-base mb-2 text-foreground">
              {getCommonText('strategyTips')}
            </h2>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground text-xs">
              {config.tips.map((key, i) => (
                <li key={i}>{getMapText(key)}</li>
              ))}
            </ul>
          </section>

          {/* 難易度設定（hasDifficultySelection が true の場合のみ） */}
          {config.hasDifficultySelection && (
            <section className="bg-primary/5 rounded-lg p-3 border border-primary/20">
              <h2 className="font-heading font-semibold text-base mb-2 text-primary">
                {(t.gameStartPuertoRico as any).difficultyLabel}
              </h2>
              {/* 難易度選択ボタン */}
              <div className="flex flex-wrap gap-1.5 mb-3">
                {DIFFICULTIES.map((diff) => (
                  <button
                    key={diff}
                    onClick={() => setSelectedDifficulty(diff)}
                    className={`px-2.5 py-1 text-xs rounded transition-all ${
                      selectedDifficulty === diff
                        ? 'bg-primary text-primary-foreground font-semibold'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    {(t.gameStartPuertoRico as any).difficulties[diff]}
                  </button>
                ))}
              </div>
              {/* キューブ割合表示 */}
              <div className="flex items-center gap-2 text-sm">
                <span className="inline-block w-3 h-3 rounded-sm bg-goods-red"></span>
                <span className="text-muted-foreground">
                  {interpolate((t.gameStartPuertoRico as any).cubeRatio, {
                    red: String(DIFFICULTY_CUBES[selectedDifficulty].red),
                    black: String(DIFFICULTY_CUBES[selectedDifficulty].black),
                  })}
                </span>
                <span className="inline-block w-3 h-3 rounded-sm bg-goods-black"></span>
              </div>
              {/* 変更時のリロード通知 */}
              {selectedDifficulty !== initialDifficulty && (
                <p className="mt-2 text-xs text-warning">
                  {(t.gameStartPuertoRico as any).difficultyChangeNotice}
                </p>
              )}
            </section>
          )}
        </div>

        {/* フッター */}
        <div className="p-4 border-t border-border flex gap-3">
          {isRulesOnly ? (
            /* ルール確認モード: 「ゲームに戻る」ボタンのみ */
            <button
              className="btn-primary flex-1 py-3 text-base font-semibold"
              onClick={onStartGame}
              data-action="return-to-game"
            >
              {getCommonText('returnToGame')}
            </button>
          ) : (
            /* ゲーム開始モード: 「マップ選択に戻る」「ゲーム開始」 */
            <>
              <a
                href={BASE_PATH}
                className="flex-1 py-3 text-base font-semibold text-center rounded-md border border-border bg-muted hover:bg-muted/80 text-muted-foreground transition-colors"
                data-action="back-to-maps"
              >
                {getCommonText('backToMaps')}
              </a>
              <button
                className="btn-primary flex-[2] py-3 text-base font-semibold"
                onClick={handleStartGame}
                data-action="start-game"
              >
                {getCommonText('startGame')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default GameStartModal;
